<template>
  <Transition name="mega-menu">
    <div
      v-show="visible"
      class="mega-menu"
      :style="panelStyle"
      role="dialog"
      aria-label="全部菜单"
      @mouseenter="handleEnter"
      @mouseleave="handleLeave"
    >
      <!-- 独立三角，始终对齐当前 hover 的一级菜单（与鼠标同一水平线） -->
      <div
        class="mega-menu__arrow"
        :style="{ top: `${arrowTop}px` }"
        aria-hidden="true"
      />

      <div ref="cardRef" class="mega-menu__card">
        <div class="mega-menu__header">
          <span class="mega-menu__title">全部菜单</span>
        </div>

        <div class="mega-menu__body">
          <div class="mega-groups">
            <template v-for="group in visibleGroups" :key="group.key">
              <section
                class="mega-group"
                :class="{ 'mega-group--active': group.key === activeKey }"
                :data-group-key="group.key"
              >
                <header class="mega-group__head">
                  <span class="mega-group__dot" :class="`mega-group__dot--${group.key}`" v-if="false"/>
                  <span class="mega-group__title">{{ group.title }}</span>
                </header>

                <!-- 二级横向 3 列；带三级的二级跨整行 -->
                <div class="mega-sections">
                  <template v-for="section in group.sections" :key="section.title">
                    <!-- 普通二级 -->
                    <div
                      v-if="!section.children || !section.children.length"
                      class="mega-section"
                      :class="{ 'mega-section--active': isActive(section) }"
                      @click="handleClickItem(section)"
                    >
                      <div class="mega-section__title-row">
                        <span class="mega-section__title">{{ section.title }}</span>
                        <span v-if="section.vip" class="mega-vip mega-vip--sm">VIP</span>
                      </div>
                      <div v-if="section.desc" class="mega-section__desc">{{ section.desc }}</div>
                    </div>

                    <!-- 带三级的二级：跨整行 -->
                    <div v-else class="mega-section mega-section--wide">
                      <div class="mega-section__title-row mega-section__title-row--wide">
                        <span class="mega-section__title mega-section__title--strong">{{ section.title }}</span>
                        <span v-if="section.vip" class="mega-vip mega-vip--sm">VIP</span>
                        <span v-if="section.desc" class="mega-section__desc mega-section__desc--inline">
                          {{ section.desc }}
                        </span>
                      </div>
                      <ul class="mega-children">
                        <li
                          v-for="child in section.children"
                          :key="child.title"
                          class="mega-child"
                          :class="{ 'mega-child--active': isActive(child) }"
                          @click="handleClickItem(child)"
                        >
                          <div class="mega-child__title-row">
                            <span class="mega-child__title">{{ child.title }}</span>
                            <span v-if="child.vip" class="mega-vip mega-vip--sm">VIP</span>
                          </div>
                          <div v-if="child.desc" class="mega-child__desc">{{ child.desc }}</div>
                        </li>
                      </ul>
                    </div>
                  </template>
                </div>
              </section>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import useUserStore from '@/store/modules/user'
import { filterMenuGroupsBySections } from './megaMenuConfig'

const props = defineProps({
  visible: { type: Boolean, default: false },
  activeKey: { type: String, default: '' },
  anchorTop: { type: Number, default: 0 },
  anchorLeft: { type: Number, default: 84 },
  groups: { type: Array, required: true },
  currentPath: { type: String, default: '' },
  currentQuery: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['select', 'enter', 'leave', 'close'])
const userStore = useUserStore()
const { permissions } = storeToRefs(userStore)

/** 浮框里始终展示所有（过滤后仍有二级菜单的）一级菜单 */
const visibleGroups = computed(() => {
  return filterMenuGroupsBySections(props.groups, permissions.value).filter(group => group.hover)
})

/* ---- 垂直定位：整块内容随鼠标上下滑动，让 hover 的分组对齐鼠标水平线 ---- */
const EDGE_MARGIN = 12
const cardRef = ref(null)
const cardHeight = ref(0)
/** 当前 hover 分组中心相对卡片顶部的偏移量 */
const activeCenterOffset = ref(0)
const viewportH = ref(typeof window !== 'undefined' ? window.innerHeight : 800)

function measure() {
  nextTick(() => {
    const card = cardRef.value
    if (!card) return
    cardHeight.value = card.offsetHeight
    const groupEl = card.querySelector(`[data-group-key="${props.activeKey}"]`)
    if (groupEl) {
      const cardRect = card.getBoundingClientRect()
      const gRect = groupEl.getBoundingClientRect()
      activeCenterOffset.value = gRect.top - cardRect.top + gRect.height / 2
    } else {
      activeCenterOffset.value = cardHeight.value / 2
    }
  })
}

/** 浮框顶部（fixed 视口坐标）：让 hover 分组中心贴齐鼠标水平线，并夹在视口内 */
const panelTop = computed(() => {
  const h = cardHeight.value || 0
  const desired = props.anchorTop - activeCenterOffset.value
  const maxTop = Math.max(EDGE_MARGIN, viewportH.value - h - EDGE_MARGIN)
  return Math.min(Math.max(desired, EDGE_MARGIN), maxTop)
})

/** 三角 top（相对卡片顶部）：夹住不越界，始终指向 hover 的图标 */
const arrowTop = computed(() => {
  const h = cardHeight.value || 0
  const rel = props.anchorTop - panelTop.value
  if (!h) return rel
  return Math.min(Math.max(rel, EDGE_MARGIN + 4), h - EDGE_MARGIN - 4)
})

const panelStyle = computed(() => ({
  '--mega-left': `${props.anchorLeft}px`,
  top: `${panelTop.value}px`,
}))

// hover 目标或内容变化时重新测量，确保对齐/夹取准确
watch(() => [props.visible, props.activeKey], measure, { flush: 'post' })

function handleEnter() { emit('enter') }
function handleLeave() { emit('leave') }
function handleClickItem(item) { emit('select', item) }

function isActive(item) {
  if (!props.currentPath || !item.path) return false
  if (props.currentPath !== item.path) return false
  if (!item.query) return true
  return Object.keys(item.query).every(
    k => String(props.currentQuery?.[k] ?? '') === String(item.query[k])
  )
}

function onKeydown(e) {
  if (e.key === 'Escape' && props.visible) emit('close')
}

function onResize() {
  viewportH.value = window.innerHeight
  measure()
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
  window.addEventListener('resize', onResize)
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', onResize)
})
</script>

<style scoped lang="scss">
.mega-menu {
  position: fixed;
  left: var(--mega-left, 84px);
  z-index: 1200;
  display: flex;
  align-items: flex-start;
  pointer-events: auto;
  /* 垂直位置（top）随鼠标平滑滑动 */
  transition: top 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  filter: drop-shadow(0 18px 40px rgba(38, 85, 241, 0.18))
    drop-shadow(0 6px 18px rgba(0, 0, 0, 0.06));
}

/* 三角独立，纵向跟随 hover 的一级图标滑动 */
.mega-menu__arrow {
  position: absolute;
  left: 0;
  width: 0;
  height: 0;
  border-top: 9px solid transparent;
  border-bottom: 9px solid transparent;
  border-right: 9px solid #fff;
  transform: translateY(-50%);
  transition: top 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  z-index: 2;
}

.mega-menu__card {
  margin-left: 8px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid rgba(38, 85, 241, 0.08);
  width: 680px;
  max-width: calc(100vw - 96px);
  max-height: calc(100vh - 24px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.mega-menu__header {
  flex: none;
  display: flex;
  align-items: center;
  padding: 14px 18px 10px;
  border-bottom: 1px solid #f0f1f4;

  .mega-menu__title {
    font-size: 13px;
    font-weight: 600;
    color: #2e2e2e;
    letter-spacing: 0.06em;
  }
}

.mega-menu__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 8px 18px 18px;
  display: flex;
  flex-direction: column;
}

/* 一级分组纵向排列 */
.mega-groups {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.mega-group {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  padding: 14px 8px 10px;
  // border-radius: 10px;
  border-bottom: 1px dashed #eef0f5;
  transition: background 0.25s ease;

  &:last-child {
    border-bottom: none;
    padding-bottom: 4px;
  }

  /* hover 的分组：蓝底强调 + 左侧色条，与鼠标对齐 */
  &--active {
    background: rgba(104, 195, 255, 0.1);
    box-shadow: inset 3px 0 0 #2655f1;

    .mega-group__title {
      color: #2655f1;
    }
    .mega-group__dot {
      transform: scale(1.4);
      box-shadow: 0 0 0 3px rgba(38, 85, 241, 0.15);
    }
  }
}

.mega-group__head {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 10px;
}

.mega-group__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2655f1;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &--monitor { background: #2655f1; }
  &--business { background: #19b27a; }
  &--it { background: #f59e0b; }
  &--system { background: #8b5cf6; }
}

.mega-group__title {
  font-size: 12px;
  font-weight: 700;
  color: #2e2e2e;
  letter-spacing: 0.06em;
  transition: color 0.2s ease;
}

/* 二级容器：3 列；带三级的二级跨整行 */
.mega-sections {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px 8px;
  align-items: start;

  /* 两条连续竖线把整个 sections 区域分成 3 列 */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 6px;
    bottom: 6px;
    width: 1px;
    background: #e8eaf0;
    pointer-events: none;
  }
  &::before { left: 33.333%; }
  &::after  { left: 66.666%; }
}

.mega-section {
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.18s ease;

  &:hover {
    background: rgba(38, 85, 241, 0.06);

    .mega-section__title {
      color: #2655f1;
    }
  }

  &--active {
    background: rgba(38, 85, 241, 0.1);

    .mega-section__title {
      color: #2655f1;
      font-weight: 600;
    }
  }

  /* 跨整行的二级（带三级）不要竖线 */
  &--wide {
    grid-column: 1 / -1;
    cursor: default;
    padding: 4px 0 0;

    &:hover {
      background: transparent;
    }
  }
}

.mega-section__title-row {
  display: flex;
  align-items: center;
  gap: 5px;

  &--wide {
    margin: 4px 0 4px;
    padding: 0 8px;
  }
}

.mega-section__title {
  font-size: 12.5px;
  font-weight: 500;
  color: #2e2e2e;
  transition: color 0.18s ease;

  &--strong {
    font-size: 11px;
    font-weight: 600;
    color: #9aa0a6;
    letter-spacing: 0.08em;
  }
}

.mega-section__desc {
  margin-top: 2px;
  font-size: 11px;
  color: #9aa0a6;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &--inline {
    margin: 0 0 0 6px;
    font-size: 11px;
    color: #b5b9c0;
    font-weight: 400;
    letter-spacing: 0;
  }
}

/* 三级 3 列 */
.mega-children {
  position: relative;
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2px 8px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 6px;
    bottom: 6px;
    width: 1px;
    background: #e8eaf0;
    pointer-events: none;
  }
  &::before { left: 33.333%; }
  &::after  { left: 66.666%; }
}

.mega-child {
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.18s ease;

  &:hover {
    background: rgba(38, 85, 241, 0.06);

    .mega-child__title {
      color: #2655f1;
    }
  }

  &--active {
    background: rgba(38, 85, 241, 0.1);

    .mega-child__title {
      color: #2655f1;
      font-weight: 600;
    }
  }
}

.mega-child__title-row {
  display: flex;
  align-items: center;
  gap: 5px;
}

.mega-child__title {
  font-size: 12.5px;
  font-weight: 500;
  color: #2e2e2e;
  transition: color 0.18s ease;
}

.mega-child__desc {
  margin-top: 2px;
  font-size: 11px;
  color: #9aa0a6;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* VIP 徽标 */
.mega-vip {
  display: inline-flex;
  align-items: center;
  height: 13px;
  padding: 0 4px;
  font-size: 9px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #f6b333 0%, #ff7e3a 100%);
  border-radius: 6px;
  letter-spacing: 0.04em;
  box-shadow: 0 1px 4px rgba(255, 126, 58, 0.32);

  &--sm {
    height: 13px;
    font-size: 9px;
    padding: 0 4px;
  }
}

/* 浮框整体动效 */
.mega-menu-enter-active,
.mega-menu-leave-active {
  transition: opacity 0.22s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.26s cubic-bezier(0.22, 1, 0.36, 1);
}
.mega-menu-enter-from,
.mega-menu-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}
.mega-menu-enter-to,
.mega-menu-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
